// src/features/fiscalization/ShiftsPanel.tsx
// Shift timeline for one fiscal point — open, close, and inspect reports.
// Covers: ListShifts, OpenShift, CloseShift, GetShiftReport.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useFiscalizationShift, useI18n } from '@react/hooks';
import { Button, Input, Badge } from '@react/components';
import type { FiscalPoint, ShiftReport } from '@core/types/fiscalization';
import { formatDateTime, formatMoney, humanizeEnum, errMessage } from './format';
import { mapShiftStatusToVariant } from './mapStatusToVariant';

export interface ShiftsPanelProps {
  fiscalPoint: FiscalPoint;
  onPointChanged: () => void;
}

export const ShiftsPanel = observer(({ fiscalPoint, onPointChanged }: ShiftsPanelProps) => {
  const store = useFiscalizationShift();
  const { t } = useI18n();
  const [openFormVisible, setOpenFormVisible] = useState(false);
  const [cashierName, setCashierName] = useState('');
  const [cashierInn, setCashierInn] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reportFor, setReportFor] = useState<string | null>(null);
  const [report, setReport] = useState<ShiftReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);

  const pointId = fiscalPoint.entity?.id;
  const hasOpenShift = !!fiscalPoint.current_shift_id;

  const load = () => {
    if (!pointId) return;
    store.ListShifts(pointId, { pagination: { cursor: '', limit: 20, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
  };

  useEffect(() => { load(); }, [pointId]);

  const handleOpen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pointId) return;
    setError(null);
    setBusy(true);
    try {
      await store.OpenShift(pointId, { entity: {} as any, fiscal_point: fiscalPoint.entity as any, cashier_name: cashierName, cashier_inn: cashierInn } as any);
      setOpenFormVisible(false);
      setCashierName('');
      setCashierInn('');
      load();
      onPointChanged();
    } catch (err) {
      setError(errMessage(err, t('fiscalization.shifts.openError')));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!pointId) return;
    setError(null);
    setBusy(true);
    try {
      await store.CloseShift(pointId, {} as any);
      load();
      onPointChanged();
    } catch (err) {
      setError(errMessage(err, t('fiscalization.shifts.closeError')));
    } finally {
      setBusy(false);
    }
  };

  const handleReport = async (shiftId: string) => {
    if (!pointId) return;
    setReportFor(shiftId);
    setReportLoading(true);
    setReport(null);
    try {
      const res = await store.GetShiftReport(pointId, shiftId);
      setReport(res.shift_report ?? null);
    } catch {
      setReport(null);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {hasOpenShift ? t('fiscalization.shifts.currentlyOpen') : t('fiscalization.shifts.currentlyClosed')}
        </span>
        {hasOpenShift ? (
          <Button size="sm" variant="secondary" isLoading={busy} onClick={handleClose}>{t('fiscalization.shifts.close')}</Button>
        ) : (
          <Button size="sm" onClick={() => setOpenFormVisible((v) => !v)}>{t('fiscalization.shifts.open')}</Button>
        )}
      </div>

      {openFormVisible && !hasOpenShift && (
        <form onSubmit={handleOpen} className="space-y-3 rounded-md border border-border p-3">
          <Input label={t('fiscalization.shifts.cashierName')} value={cashierName} onChange={(e) => setCashierName(e.target.value)} required />
          <Input label={t('fiscalization.shifts.cashierInn')} value={cashierInn} onChange={(e) => setCashierInn(e.target.value)} />
          <Button type="submit" size="sm" isLoading={busy}>{t('fiscalization.shifts.open')}</Button>
        </form>
      )}

      <div>
        <h4 className="text-sm font-medium text-foreground mb-2">{t('fiscalization.shifts.history')}</h4>
        {store.isLoading ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : store.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('fiscalization.shifts.empty')}</p>
        ) : (
          <ul className="space-y-2">
            {store.items.map((s) => (
              <li key={s.entity?.id} className="rounded-md border border-border p-3 text-sm space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-foreground">{t('fiscalization.shifts.shiftNumber', { n: s.shift_number })}</span>
                  <Badge variant={mapShiftStatusToVariant(s.status)} size="sm">{humanizeEnum(s.status, 'SHIFT_STATUS_')}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">{formatDateTime(s.created_at)}{s.closed_at ? ` – ${formatDateTime(s.closed_at)}` : ''}</p>
                <p className="text-xs text-muted-foreground">{t('fiscalization.shifts.receiptCount', { n: s.receipt_count })}</p>
                {s.status === 'SHIFT_STATUS_CLOSED' && s.entity?.id && (
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => handleReport(s.entity!.id)}>
                    {reportFor === s.entity.id ? t('fiscalization.shifts.hideReport') : t('fiscalization.shifts.viewReport')}
                  </button>
                )}
                {reportFor === s.entity?.id && (
                  reportLoading ? (
                    <p className="text-xs text-muted-foreground">{t('common.loading')}</p>
                  ) : report ? (
                    <dl className="grid grid-cols-2 gap-1 text-xs pt-1 border-t border-border mt-1">
                      <dt className="text-muted-foreground">{t('fiscalization.shifts.totalSell')}</dt><dd>{formatMoney(report.total_sell)}</dd>
                      <dt className="text-muted-foreground">{t('fiscalization.shifts.totalCash')}</dt><dd>{formatMoney(report.total_cash)}</dd>
                      <dt className="text-muted-foreground">{t('fiscalization.shifts.totalElectronic')}</dt><dd>{formatMoney(report.total_electronic)}</dd>
                      <dt className="text-muted-foreground">{t('fiscalization.shifts.totalReceipts')}</dt><dd>{report.total_receipts}</dd>
                    </dl>
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('fiscalization.shifts.reportUnavailable')}</p>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
});

export default ShiftsPanel;
